import { faEye, faHistory, faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

/**
 * Summary info bar showing ticket category, sequence type, and historical data
 * Shows in edit/view modes and add mode Step 3
 */
function InvoiceSummaryInfoBar({
    formValues,
    previousInvoices = [],
    onShowPreviousInvoices,
    onShowHistoricalInvoices,
    compact = false,
}) {
    const { t } = useTranslation();

    // Calculate actual count excluding current invoice if it exists in the array
    const actualPreviousCount = previousInvoices.filter(
        invoice => invoice.id !== formValues.id
    ).length;

    // Determine if this is a first invoice (no previous invoices in this category)
    const isFirstInvoice = actualPreviousCount === 0;

    // Determine if historical baseline was configured
    const hasHistoricalBaseline = formValues.isHistoricalBaseline === true;

    if (compact) {
        return (
            <div className="d-flex align-items-center gap-3 flex-wrap">
                {/* Ticket Category */}
                <div className="d-flex align-items-center gap-2">
                    {actualPreviousCount > 0 ? (
                        <button
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            onClick={onShowPreviousInvoices}
                            title={t("invoice.view-previous-invoices")}
                        >
                            <span className="badge bg-primary d-inline-flex align-items-center">
                                {formValues.selectedTicketCategory?.label || t("common.not-available")}
                                <span className="ms-1 badge bg-light text-dark">{actualPreviousCount}</span>
                                <FontAwesomeIcon icon={faEye} className="ms-1" />
                            </span>
                        </button>
                    ) : (
                        <span className="badge bg-primary d-inline-flex align-items-center">
                            {formValues.selectedTicketCategory?.label || t("common.not-available")}
                        </span>
                    )}
                </div>
                {isFirstInvoice && (
                    <div className="d-flex align-items-center gap-2">
                        {hasHistoricalBaseline ? (
                            <>
                                <span className="badge bg-info d-inline-flex align-items-center">
                                    <FontAwesomeIcon icon={faHistory} className="me-1" />
                                    {t("invoice.continue-from-previous")}
                                </span>
                                {formValues.historicalInvoices?.length > 0 && (
                                    <button
                                        className="btn btn-link btn-sm p-0 text-decoration-none"
                                        onClick={onShowHistoricalInvoices}
                                    >
                                        <span className="badge bg-dark d-inline-flex align-items-center">
                                            {formValues.historicalInvoices.length} {t("invoice.historical-invoices")}
                                            <FontAwesomeIcon icon={faEye} className="ms-1" />
                                        </span>
                                    </button>
                                )}
                            </>
                        ) : (
                            <span className="badge bg-secondary d-inline-flex align-items-center">
                                <FontAwesomeIcon icon={faSync} className="me-1" />
                                {t("invoice.start-from-az1")}
                            </span>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="bg-light border-bottom p-2 mt-2 mb-2">
            <div className="d-flex align-items-center gap-3 flex-wrap">
                {/* Ticket Category - Always shown */}
                <div className="d-flex align-items-center gap-2">
                    <small className="text-muted">
                        <strong>{t("invoice.ticket-category")}:</strong>
                    </small>
                    {actualPreviousCount > 0 ? (
                        <button
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            onClick={onShowPreviousInvoices}
                            title={t("invoice.view-previous-invoices")}
                        >
                            <span className="badge bg-primary d-inline-flex align-items-center">
                                {formValues.selectedTicketCategory?.label || t("common.not-available")}
                                <span className="ms-1 badge bg-light text-dark">{actualPreviousCount}</span>
                                <FontAwesomeIcon icon={faEye} className="ms-1" />
                            </span>
                        </button>
                    ) : (
                        <span className="badge bg-primary d-inline-flex align-items-center">
                            {formValues.selectedTicketCategory?.label || t("common.not-available")}
                        </span>
                    )}
                </div>

                {/* Sequence Type Info - Only show for first invoice in category */}
                {isFirstInvoice && (
                    <div className="d-flex align-items-center gap-2">
                        {hasHistoricalBaseline ? (
                            <>
                                {/* Historical Baseline: Continue from previous invoices */}
                                <small className="text-muted">
                                    <FontAwesomeIcon icon={faHistory} className="me-1" />
                                    <strong>{t("invoice.sequence-type")}:</strong>
                                </small>
                                <span className="badge bg-info d-inline-flex align-items-center">
                                    {t("invoice.continue-from-previous")}
                                </span>
                                {formValues.historicalInvoices?.length > 0 && (
                                    <button
                                        className="btn btn-link btn-sm p-0 text-decoration-none"
                                        onClick={onShowHistoricalInvoices}
                                        title={t("invoice.view-historical-invoices")}
                                    >
                                        <span className="badge bg-dark d-inline-flex align-items-center">
                                            {formValues.historicalInvoices.length} {t("invoice.historical-invoices")}
                                            <FontAwesomeIcon icon={faEye} className="ms-1" />
                                        </span>
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                {/* First invoice: Start from AZ1 */}
                                <small className="text-muted">
                                    <FontAwesomeIcon icon={faSync} className="me-1" />
                                    <strong>{t("invoice.sequence-type")}:</strong>
                                </small>
                                <span className="badge bg-secondary d-inline-flex align-items-center">
                                    {t("invoice.start-from-az1")}
                                </span>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default InvoiceSummaryInfoBar;
